/* eslint-disable react-refresh/only-export-components */
/* eslint-disable unused-imports/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { Grid, GridProps, Box } from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import ExpandableSummaryWrapper, { ExpandableSummaryWrapperProps } from '../expandable-summary-wrapper/expandable-summary-wrapper';
// import { ExpandableSummaryWrapper, ExpandableSummaryWrapperProps } from './ExpandableSummaryWrapper';

interface DynamicLayoutWrapperProps {
  // Main content props
  children: React.ReactNode;
  mainContentProps?: Omit<GridProps, 'item' | 'xs'>;
  
  // Summary wrapper props
  summaryContent: React.ReactNode;
  summaryProps?: Omit<ExpandableSummaryWrapperProps, 'children' | 'onToggle'>;
  
  // Layout configuration
  maxGridSize?: number;
  defaultSummaryExpanded?: boolean;
  expandedSummarySize?: number;
  collapsedSummarySize?: number;
  
  // Container props
  containerProps?: Omit<GridProps, 'container'>;
  
  // Callbacks
  onLayoutChange?: (mainSize: number, summarySize: number, isExpanded: boolean) => void;
  
  // Animation configuration
  enableTransitions?: boolean;
  transitionDuration?: number | string;
  transitionEasing?: string;
  storageKey?: string;
  fillHeight?: boolean;
}

export const DynamicLayoutWrapper: React.FC<DynamicLayoutWrapperProps> = ({
  children,
  mainContentProps = {},
  summaryContent,
  summaryProps = {},
  maxGridSize = 12,
  defaultSummaryExpanded = true,
  expandedSummarySize = 3,
  collapsedSummarySize = 0,
  containerProps = {},
  onLayoutChange,
  enableTransitions = true,
  transitionDuration,
  transitionEasing,
  storageKey = 'grid-summary-panel-state',
  fillHeight = true ,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const getInitialState = useCallback(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : defaultSummaryExpanded;
    }
    return defaultSummaryExpanded;
  }, [defaultSummaryExpanded, storageKey]);
  
  // Calculate initial main content size
  const initialMainSize = useMemo(() => {
    return (isMobile || !getInitialState()) ? maxGridSize :(defaultSummaryExpanded 
      ? maxGridSize - expandedSummarySize 
      : maxGridSize - collapsedSummarySize);
  }, [isMobile, getInitialState, maxGridSize, defaultSummaryExpanded, expandedSummarySize, collapsedSummarySize]);
  
  const [mainContentSize, setMainContentSize] = useState<number>(initialMainSize);
  
  // Handle summary toggle with enhanced logic
  const handleSummaryToggle = useCallback((isExpanded: boolean, summaryGridSize: number) => {
    if(isMobile) {
      setMainContentSize(maxGridSize);
      return 
    }
    const newMainContentSize = Math.max(1, Math.min(maxGridSize - summaryGridSize, maxGridSize));
    
    setMainContentSize(newMainContentSize);
    
    // Notify parent component of layout changes
    onLayoutChange?.(newMainContentSize, summaryGridSize, isExpanded);
  }, [isMobile, maxGridSize, onLayoutChange]);
  
  // Create transition styles
  const transitionStyles = useMemo(() => {
    if (!enableTransitions) return {};
    
    const duration = transitionDuration || theme.transitions.duration.enteringScreen;
    const easing = transitionEasing || theme.transitions.easing.sharp;
    
    return {
      transition: theme.transitions.create(['flex-basis', 'max-width'], {
        easing,
        duration,
      }),
    };
  }, [enableTransitions, theme, transitionDuration, transitionEasing]);
  
  // Fill height measurement
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [remainingHeight, setRemainingHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (!fillHeight) return;

    const measure = () => {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        setRemainingHeight(window.innerHeight - rect.top);
      }
    };
    measure();

    const observer = new ResizeObserver(measure);
    if (wrapperRef.current) {
      observer.observe(wrapperRef.current);
    }
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [fillHeight]);

  // Merge summary props with defaults
  const mergedSummaryProps: ExpandableSummaryWrapperProps = {
    defaultExpanded: defaultSummaryExpanded,
    expandedGridSize: expandedSummarySize,
    collapsedGridSize: collapsedSummarySize,
    persistState: true,
    ariaLabel: 'Toggle summary panel',
    ...summaryProps,
    onToggle: handleSummaryToggle,
    children: summaryContent,
    storageKey: storageKey
  };
  const isFill = fillHeight && remainingHeight !== undefined;

  const mergedSummaryPropsWithFill: ExpandableSummaryWrapperProps = isFill
    ? { ...mergedSummaryProps,  sx: { height: '100%', overflow: 'visible' } }
    : mergedSummaryProps;

  const grid = (
    <Grid container {...containerProps} sx={{
      height: isFill ? '100%' : undefined,
      alignItems: isFill ? 'flex-start' : undefined,
    }}>
      <Grid
        item
        xs={mainContentSize}
        sx={{
          ...transitionStyles,
          ...mainContentProps.sx,
          ...(isFill ? {
            overflowY: 'auto',
            maxHeight: '100%',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          } : {}),
        }}
        {...mainContentProps}
      >
        {children}
      </Grid>
      
      <ExpandableSummaryWrapper  {...mergedSummaryPropsWithFill} />
    </Grid>
  );

  if (fillHeight) {
    return (
      <Box ref={wrapperRef} sx={{ height: remainingHeight }}>
        {grid}
      </Box>
    );
  }

  return grid;
};

// Enhanced version with additional features
interface AdvancedDynamicLayoutWrapperProps extends DynamicLayoutWrapperProps {
  // Responsive breakpoints
  responsiveConfig?: {
    xs?: { expandedSize: number; collapsedSize: number };
    sm?: { expandedSize: number; collapsedSize: number };
    md?: { expandedSize: number; collapsedSize: number };
    lg?: { expandedSize: number; collapsedSize: number };
    xl?: { expandedSize: number; collapsedSize: number };
  };
  
  // Multiple summary panels support
  multipleSummaries?: Array<{
    content: React.ReactNode;
    props?: Omit<ExpandableSummaryWrapperProps, 'children' | 'onToggle'>;
    id: string;
  }>;
  
  // Layout modes
  layoutMode?: 'horizontal' | 'vertical' | 'adaptive';
  
  // Minimum sizes
  minMainContentSize?: number;
  minSummarySize?: number;
}

export const AdvancedDynamicLayoutWrapper: React.FC<AdvancedDynamicLayoutWrapperProps> = ({
  multipleSummaries = [],
  layoutMode = 'horizontal',
  minMainContentSize = 4,
  minSummarySize = 2,
  responsiveConfig,
  ...baseProps
}) => {
  const [summaryStates, setSummaryStates] = useState<Record<string, { size: number; expanded: boolean }>>({});
  
  // Calculate total summary size
  const totalSummarySize = useMemo(() => {
    return Object.values(summaryStates).reduce((total, state) => total + state.size, 0);
  }, [summaryStates]);
  
  // Enhanced toggle handler for multiple summaries
  const handleMultipleSummaryToggle = useCallback((
    summaryId: string,
    isExpanded: boolean,
    summaryGridSize: number
  ) => {
    setSummaryStates(prev => {
      const newStates = {
        ...prev,
        [summaryId]: { size: summaryGridSize, expanded: isExpanded }
      };
      
      const newTotalSize = Object.values(newStates).reduce((total, state) => total + state.size, 0);
      const newMainSize = Math.max(
        minMainContentSize,
        Math.min(baseProps.maxGridSize! - newTotalSize, baseProps.maxGridSize!)
      );
      
      baseProps.onLayoutChange?.(newMainSize, newTotalSize, isExpanded);
      
      return newStates;
    });
  }, [baseProps, minMainContentSize]);
  
  // If multiple summaries are provided, render advanced layout
  if (multipleSummaries.length > 0) {
    const mainSize = Math.max(
      minMainContentSize,
      (baseProps.maxGridSize || 12) - totalSummarySize
    );
    
    return (
      <Grid container {...baseProps.containerProps}>
        <Grid
          item
          xs={mainSize}
          sx={{
            transition: baseProps.enableTransitions ? 
              `all ${baseProps.transitionDuration || 300}ms ${baseProps.transitionEasing || 'ease-in-out'}` : 
              undefined,
            ...baseProps.mainContentProps?.sx,
          }}
          {...baseProps.mainContentProps}
        >
          {baseProps.children}
        </Grid>
        
        {multipleSummaries.map((summary) => (
          <ExpandableSummaryWrapper
            key={summary.id}
            {...summary.props}
            onToggle={(isExpanded, size) => 
              handleMultipleSummaryToggle(summary.id, isExpanded, size)
            }
          >
            {summary.content}
          </ExpandableSummaryWrapper>
        ))}
      </Grid>
    );
  }
  
  // Fallback to basic layout
  return <DynamicLayoutWrapper {...baseProps} />;
};

// Hook for managing layout state externally
export const useDynamicLayout = (
  maxGridSize: number = 12,
  defaultSummaryExpanded: boolean = true,
  expandedSize: number = 3,
  collapsedSize: number = 0
) => {
  const [isExpanded, setIsExpanded] = useState(defaultSummaryExpanded);
  const [summarySize, setSummarySize] = useState(
    defaultSummaryExpanded ? expandedSize : collapsedSize
  );
  const [mainSize, setMainSize] = useState(
    maxGridSize - (defaultSummaryExpanded ? expandedSize : collapsedSize)
  );
  
  const toggleSummary = useCallback(() => {
    const newExpanded = !isExpanded;
    const newSummarySize = newExpanded ? expandedSize : collapsedSize;
    const newMainSize = maxGridSize - newSummarySize;
    
    setIsExpanded(newExpanded);
    setSummarySize(newSummarySize);
    setMainSize(newMainSize);
  }, [isExpanded, expandedSize, collapsedSize, maxGridSize]);
  
  const setSummaryState = useCallback((expanded: boolean) => {
    const newSummarySize = expanded ? expandedSize : collapsedSize;
    const newMainSize = maxGridSize - newSummarySize;
    
    setIsExpanded(expanded);
    setSummarySize(newSummarySize);
    setMainSize(newMainSize);
  }, [expandedSize, collapsedSize, maxGridSize]);
  
  return {
    isExpanded,
    summarySize,
    mainSize,
    toggleSummary,
    setSummaryState,
  };
};

// Utility function for calculating optimal sizes
export const calculateOptimalSizes = (
  totalSize: number,
  contentRatio: number = 0.75,
  minContentSize: number = 4,
  minSummarySize: number = 2
) => {
  const idealContentSize = Math.floor(totalSize * contentRatio);
  const idealSummarySize = totalSize - idealContentSize;
  
  const contentSize = Math.max(minContentSize, idealContentSize);
  const summarySize = Math.max(minSummarySize, Math.min(idealSummarySize, totalSize - contentSize));
  
  return {
    contentSize: Math.min(contentSize, totalSize - summarySize),
    summarySize,
    totalUsed: contentSize + summarySize,
  };
};

export default DynamicLayoutWrapper;